/**
 * Thread — daily 3-question check-in (The Thread feature).
 *
 * Flow: Q1 → Q2 → Q3 → mandatory grounding close → done.
 * Auto-saves after each question. Detects crisis signals inline.
 * Optional add-ons (intention / gratitude / release) shown collapsed.
 * No streaks. Read-only past entries via /thread/history (future).
 */
import { useEffect, useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import type { User } from '@supabase/supabase-js'
import { db } from '../lib/supabase'
import {
  saveThreadEntry, fetchTodayEntry, triggerDigest, detectCrisis,
  type ThreadResponses,
} from '../lib/thread'

interface Props { user: User }

/*
 * Crisis resources.
 *
 * Was a module-level JSX constant with the US helplines hardcoded, which meant
 * two problems: a constant cannot call t(), and a Polish user in crisis was
 * shown numbers that do not work in Poland. Now a component, reading the
 * heading and the numbers from the locale — crisis.resources carries the
 * Polish lines Marta verified (116 123, 116 111, and Fundacja ITAKA).
 *
 * Rendered line by line rather than as one block so each helpline stays on its
 * own row regardless of how many a locale lists.
 */
function CrisisResources() {
  const { t } = useTranslation()
  const lines = t('crisis.resources').split('\n').filter(Boolean)
  return (
    <div style={{
      background: '#FFF5F5', border: '1.5px solid #FCA5A5', borderRadius: '0.875rem',
      padding: '1rem 1.25rem', marginTop: '0.875rem',
    }}>
      <p style={{ fontSize: '0.875rem', color: '#991B1B', fontWeight: 500, margin: '0 0 0.375rem' }}>
        {t('crisis.heading')}
      </p>
      <div style={{ fontSize: '0.8125rem', color: '#7F1D1D', lineHeight: 1.65 }}>
        {lines.map((line, i) => <p key={i} style={{ margin: 0 }}>{line}</p>)}
      </div>
    </div>
  )
}

export default function Thread({ user }: Props) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [responses, setResponses] = useState<ThreadResponses>({})
  const [entryId, setEntryId]     = useState<string | null>(null)
  const [showCrisis, setShowCrisis] = useState(false)
  const [phase, setPhase]         = useState<'questions' | 'close' | 'done'>('questions')
  const [showOptional, setShowOptional] = useState(false)
  const [loading, setLoading]     = useState(true)
  const [tier, setTier]           = useState<string>('free')
  const saveTimer                 = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load today's existing entry + user tier
  useEffect(() => {
    Promise.all([
      fetchTodayEntry(user.id),
      db().from('profiles').select('subscription_tier').eq('id', user.id).maybeSingle(),
    ]).then(([existing, profileRes]) => {
      if (existing) {
        setResponses(existing.responses)
        setEntryId(existing.id)
        if (existing.responses.emotion && existing.responses.trigger && existing.responses.familiar) {
          setPhase('close')
        }
      }
      const loadedTier = (profileRes.data as { subscription_tier: string } | null)?.subscription_tier ?? 'free'
      setTier(loadedTier)
      setLoading(false)
    })
  }, [user.id])

  // Tier gate — Practice+ only
  useEffect(() => {
    if (!loading && tier === 'free') navigate('/')
  }, [loading, tier, navigate])

  const debouncedSave = useCallback((updated: ThreadResponses) => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      const crisisFlag = detectCrisis(updated.emotion ?? '') || detectCrisis(updated.trigger ?? '')
      setShowCrisis(crisisFlag)
      const saved = await saveThreadEntry(user.id, updated, crisisFlag)
      if (saved) {
        setEntryId(saved.id)
        if (saved.daily_digest === null) triggerDigest(saved.id)
      }
    }, 800)
  }, [user.id])

  function set(field: keyof ThreadResponses, value: string) {
    const updated = { ...responses, [field]: value }
    setResponses(updated)
    debouncedSave(updated)
  }

  const coreComplete = !!(responses.emotion?.trim() && responses.trigger?.trim() && responses.familiar?.trim())

  const TA_STYLE: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    padding: '0.875rem 1rem', borderRadius: '0.875rem',
    border: '1.5px solid #E4D5EE', fontSize: '1rem',
    color: '#1C1128', lineHeight: 1.7, resize: 'none',
    outline: 'none', fontFamily: 'inherit',
    background: 'white', minHeight: '4.5rem',
    transition: 'border-color 0.15s',
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100svh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAF7F4' }}>
        <div style={{ width: '1.25rem', height: '1.25rem', borderRadius: '50%', border: '2px solid #3D1F62', borderTopColor: 'transparent' }} className="animate-spin" />
      </div>
    )
  }

  if (phase === 'done') {
    return (
      <div style={{ minHeight: '100svh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAF7F4', padding: '2rem' }}>
        <div style={{ textAlign: 'center', maxWidth: '20rem' }}>
          <div style={{ width: '3rem', height: '3rem', borderRadius: '50%', background: 'linear-gradient(135deg, #9B72C0, #4A80C0)', margin: '0 auto 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M4 9l4 4 6-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p style={{ fontFamily: 'var(--font-playfair, Georgia, serif)', fontSize: '1.25rem', color: '#1C1128', margin: '0 0 0.75rem' }}>
            {t('thread.showedUpToday')}
          </p>
          <p style={{ fontSize: '0.9rem', color: '#6B5F7A', lineHeight: 1.7, margin: '0 0 2rem' }}>
            {t('thread.wholePractice')}
          </p>
          <button
            onClick={() => navigate('/')}
            style={{ padding: '0.75rem 2rem', borderRadius: '9999px', background: '#3D1F62', color: 'white', fontSize: '0.9375rem', fontWeight: 500, border: 'none', cursor: 'pointer' }}
          >
            {t('thread.backHome')}
          </button>
        </div>
      </div>
    )
  }

  if (phase === 'close') {
    return (
      <div style={{ minHeight: '100svh', background: '#FAF7F4', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: '26rem', width: '100%', textAlign: 'center' }}>
          <p style={{ fontFamily: 'var(--font-playfair, Georgia, serif)', fontSize: '1.375rem', color: '#1C1128', lineHeight: 1.5, margin: '0 0 1rem' }}>
            {t('thread.beforeYouClose')}
          </p>
          <p style={{ fontSize: '1rem', color: '#6B5F7A', lineHeight: 1.8, margin: '0 0 2rem' }}>
            {t('thread.namedSomethingTrue')}
            <br />{t('thread.thatsEnough')}
          </p>
          <button
            onClick={() => setPhase('done')}
            style={{ padding: '0.875rem 2.5rem', borderRadius: '9999px', background: '#3D1F62', color: 'white', fontSize: '1rem', fontWeight: 500, border: 'none', cursor: 'pointer' }}
          >
            {t('thread.done')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100svh', background: '#FAF7F4', fontFamily: 'var(--font-inter, system-ui, sans-serif)' }}>

      {/* Header */}
      <header style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', borderBottom: '1px solid #E4D5EE', background: 'white' }}>
        <button
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9B8FAA', padding: '0.25rem', lineHeight: 0 }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M13 16l-6-6 6-6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div>
          <p style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9B72C0', margin: 0 }}>
            {t('thread.heading')}
          </p>
          <p style={{ fontSize: '0.8125rem', color: '#9B8FAA', margin: 0 }}>
            {new Date().toLocaleDateString(i18n.language, { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </header>

      <div style={{ maxWidth: '32rem', margin: '0 auto', padding: '2rem 1.5rem' }}>

        {/* Q1 */}
        <div style={{ marginBottom: '1.75rem' }}>
          <label style={{ display: 'block', fontSize: '1.0625rem', color: '#1C1128', fontWeight: 400, lineHeight: 1.5, marginBottom: '0.75rem', fontFamily: 'var(--font-playfair, Georgia, serif)' }}>
            {t('thread.q1')}
          </label>
          <textarea
            style={TA_STYLE}
            value={responses.emotion ?? ''}
            onChange={e => set('emotion', e.target.value)}
            placeholder={t('thread.q1PlaceholderNew')}
            rows={3}
            onFocus={e => (e.target.style.borderColor = '#9B72C0')}
            onBlur={e => (e.target.style.borderColor = '#E4D5EE')}
          />
          {showCrisis && responses.emotion && <CrisisResources />}
        </div>

        {/* Q2 — shows after Q1 has content */}
        {(responses.emotion || responses.trigger) && (
          <div style={{ marginBottom: '1.75rem' }}>
            <label style={{ display: 'block', fontSize: '1.0625rem', color: '#1C1128', fontWeight: 400, lineHeight: 1.5, marginBottom: '0.75rem', fontFamily: 'var(--font-playfair, Georgia, serif)' }}>
              {t('thread.q2')}
            </label>
            <textarea
              style={TA_STYLE}
              value={responses.trigger ?? ''}
              onChange={e => set('trigger', e.target.value)}
              placeholder={t('thread.q2PlaceholderNew')}
              rows={2}
              onFocus={e => (e.target.style.borderColor = '#9B72C0')}
              onBlur={e => { e.target.style.borderColor = '#E4D5EE'; if (detectCrisis(e.target.value)) setShowCrisis(true) }}
            />
            {showCrisis && responses.trigger && <CrisisResources />}
          </div>
        )}

        {/* Q3 — shows after Q2 has content */}
        {(responses.trigger || responses.familiar) && (
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', fontSize: '1.0625rem', color: '#1C1128', fontWeight: 400, lineHeight: 1.5, marginBottom: '0.75rem', fontFamily: 'var(--font-playfair, Georgia, serif)' }}>
              {t('thread.q3')}
            </label>
            <textarea
              style={TA_STYLE}
              value={responses.familiar ?? ''}
              onChange={e => set('familiar', e.target.value)}
              placeholder={t('thread.q3PlaceholderNew')}
              rows={2}
              onFocus={e => (e.target.style.borderColor = '#9B72C0')}
              onBlur={e => (e.target.style.borderColor = '#E4D5EE')}
            />
          </div>
        )}

        {/* Optional add-ons */}
        {coreComplete && (
          <div style={{ marginBottom: '2rem' }}>
            <button
              onClick={() => setShowOptional(s => !s)}
              style={{ fontSize: '0.875rem', color: '#9B72C0', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '0.375rem' }}
            >
              <span>{showOptional ? '▾' : '▸'}</span>
              {showOptional ? t('thread.hide') : t('thread.addMore')}
            </button>

            {showOptional && (
              <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid #E4D5EE' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', color: '#6B5F7A', marginBottom: '0.5rem' }}>
                    {t('thread.intentionLabel')} <span style={{ color: '#C4B5D4' }}>{t('common.optional')}</span>
                  </label>
                  <textarea style={{ ...TA_STYLE, minHeight: '3rem' }} rows={2} value={responses.intention ?? ''} onChange={e => set('intention', e.target.value)} placeholder={t('thread.intentionPlaceholderNew')} onFocus={e => (e.target.style.borderColor = '#9B72C0')} onBlur={e => (e.target.style.borderColor = '#E4D5EE')} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', color: '#6B5F7A', marginBottom: '0.5rem' }}>
                    {t('thread.gratitudeLabel')} <span style={{ color: '#C4B5D4' }}>{t('common.optional')}</span>
                  </label>
                  <textarea style={{ ...TA_STYLE, minHeight: '3rem' }} rows={2} value={responses.gratitude ?? ''} onChange={e => set('gratitude', e.target.value)} placeholder={t('thread.gratitudePlaceholderNew')} onFocus={e => (e.target.style.borderColor = '#9B72C0')} onBlur={e => (e.target.style.borderColor = '#E4D5EE')} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', color: '#6B5F7A', marginBottom: '0.5rem' }}>
                    {t('thread.releaseLabel')} <span style={{ color: '#C4B5D4' }}>{t('common.optional')}</span>
                  </label>
                  <textarea style={{ ...TA_STYLE, minHeight: '3rem' }} rows={2} value={responses.release ?? ''} onChange={e => set('release', e.target.value)} placeholder={t('thread.releasePlaceholderNew')} onFocus={e => (e.target.style.borderColor = '#9B72C0')} onBlur={e => (e.target.style.borderColor = '#E4D5EE')} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Close CTA */}
        {coreComplete && (
          <button
            onClick={() => setPhase('close')}
            style={{ width: '100%', padding: '1rem', borderRadius: '0.875rem', background: '#3D1F62', color: 'white', fontSize: '1rem', fontWeight: 500, border: 'none', cursor: 'pointer' }}
          >
            {t('thread.closeForToday')}
          </button>
        )}

        {/* Entry count — quiet, not streak */}
        {entryId && (
          <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#C4B5D4', marginTop: '1.5rem' }}>
            {t('thread.saved')}
          </p>
        )}
      </div>
    </div>
  )
}
